import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3010/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Device licensing.
 *
 * This install's token is what tells the API which shop it belongs to and that
 * it is one of the shop's licensed devices. It replaces the single shared
 * register key, which every shop would otherwise have had to share.
 *
 * Browser storage is clearable, so it is written to two places and the server
 * can re-issue it from the device fingerprint when both are gone.
 */
const DEVICE_TOKEN_KEY = "device_token";
const STAFF_TOKEN_KEY = "staff_token";

export const deviceToken = {
  get: () => {
    try {
      return localStorage.getItem(DEVICE_TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (token) => {
    try {
      localStorage.setItem(DEVICE_TOKEN_KEY, token);
    } catch {
      // A private window can refuse storage; the session still works
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(DEVICE_TOKEN_KEY);
    } catch {
      /* nothing to clear */
    }
  },
};

/**
 * Owners of several shops: one phone may be a licensed device in each of them, so
 * this install keeps one device token per shop and says which shop it is on now.
 * `device_token` above stays the current one, so everything that reads it — the
 * register, the socket — is unchanged. A shop is switched by putting its token
 * there and reloading (utils/shopSwitch.js).
 */
const CURRENT_SHOP_KEY = "current_shop_id";
const DEVICE_TOKENS_KEY = "device_tokens";
const DEVICE_LABEL_KEY = "device_label";

const readJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeRaw = (key, value) => {
  try {
    if (value === null || value === undefined) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* a private window: this shop's token still works until the tab closes */
  }
};

export const shopSession = {
  /** The shop this install is on, once known (null on a device from before shop switching). */
  currentId: () => {
    try {
      return localStorage.getItem(CURRENT_SHOP_KEY);
    } catch {
      return null;
    }
  },
  tokenFor: (shopId) => readJson(DEVICE_TOKENS_KEY, {})[shopId] || null,
  /** Remembers this shop's token and makes it the one in use. */
  use: (shopId, token) => {
    writeRaw(DEVICE_TOKENS_KEY, JSON.stringify({ ...readJson(DEVICE_TOKENS_KEY, {}), [shopId]: token }));
    writeRaw(CURRENT_SHOP_KEY, shopId);
    deviceToken.set(token);
  },
  /** A shop's token the server no longer honours (revoked): forgotten, so switching asks again. */
  forget: (shopId) => {
    const tokens = readJson(DEVICE_TOKENS_KEY, {});
    delete tokens[shopId];
    writeRaw(DEVICE_TOKENS_KEY, JSON.stringify(tokens));
  },
  /**
   * Notes which shop the token in use belongs to, the first time the app learns
   * it (a device activated before shop switching existed).
   */
  record: (shopId) => {
    const token = deviceToken.get();
    if (!shopId || !token) return;
    if (shopSession.currentId() === shopId && shopSession.tokenFor(shopId) === token) return;
    shopSession.use(shopId, token);
  },
  /** The name this install was given when it was first activated, reused in another shop. */
  label: () => {
    try {
      return localStorage.getItem(DEVICE_LABEL_KEY);
    } catch {
      return null;
    }
  },
  setLabel: (label) => writeRaw(DEVICE_LABEL_KEY, label),
};

const STAFF_INFO_KEY = "staff_info";

export const staffToken = {
  get: () => {
    try {
      return localStorage.getItem(STAFF_TOKEN_KEY);
    } catch {
      return null;
    }
  },
  /**
   * The token is what the server trusts; the cashier's name is kept beside it
   * only so a page reload still shows who is serving. Without it the header
   * would say "sign in" while sales were still being attributed.
   *
   * Kept in localStorage, not sessionStorage: the PIN unlock is meant to
   * last the whole shift (12h, the token's own expiry — see isExpired),
   * surviving the tab or the browser being closed and reopened, not just
   * reloaded.
   */
  set: (token, staff) => {
    try {
      localStorage.setItem(STAFF_TOKEN_KEY, token);
      if (staff) localStorage.setItem(STAFF_INFO_KEY, JSON.stringify(staff));
    } catch {
      /* the shift just goes unattributed */
    }
  },
  staff: () => {
    try {
      const raw = localStorage.getItem(STAFF_INFO_KEY);
      return raw &&
        localStorage.getItem(STAFF_TOKEN_KEY) &&
        !staffToken.isExpired()
        ? JSON.parse(raw)
        : null;
    } catch {
      return null;
    }
  },
  /**
   * Whether the cashier on the till may do something ("power.extend_credit").
   * Only decides what the till offers; the server checks again, fresh.
   *
   * A shift unlocked before permissions existed carries can_* flags instead —
   * honoured until that token runs out (12h at most), then this falls away.
   */
  staffCan: (staff, permission) => {
    if (!staff) return false;
    if (Array.isArray(staff.permissions)) return staff.permissions.includes(permission);
    if (permission === "power.extend_credit") return Boolean(staff.can_extend_credit);
    if (permission === "power.refund") return Boolean(staff.can_refund);
    return false;
  },
  /**
   * When the server stops honouring this shift, read from the token itself.
   * The server still checks the signature — this only lets the till lock at
   * the moment the server would stop naming the cashier, instead of carrying
   * on with sales that quietly record nobody.
   */
  expiresAt: (token = staffToken.get()) => {
    if (!token) return null;

    try {
      const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
      const padded = part + "=".repeat((4 - (part.length % 4)) % 4);
      const { exp } = JSON.parse(atob(padded));
      return exp ? exp * 1000 : null;
    } catch {
      return null;
    }
  },
  isExpired: (token = staffToken.get()) => {
    const expiresAt = staffToken.expiresAt(token);
    return expiresAt !== null && expiresAt <= Date.now();
  },
  clear: () => {
    try {
      localStorage.removeItem(STAFF_TOKEN_KEY);
      localStorage.removeItem(STAFF_INFO_KEY);
    } catch {
      /* nothing to clear */
    }
  },
};

/**
 * The signals the server hashes into a device fingerprint. None of it
 * identifies a person; it only has to tell this tablet from that laptop.
 */
export const deviceFingerprint = () => ({
  user_agent: navigator.userAgent,
  platform: navigator.platform,
  screen: `${window.screen.width}x${window.screen.height}x${window.devicePixelRatio}`,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  language: navigator.language,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // A request made for another of the user's shops (activating this device
  // there, freeing one of its slots) names it and carries no device token: the
  // token would pin the request to the shop this install is on now
  if (config.shopId) {
    config.headers["x-shop-id"] = config.shopId;
  } else {
    const device = deviceToken.get();
    if (device) {
      config.headers["x-device-token"] = device;
    }
  }

  // A sale replayed from the offline queue brings the token it was rung up
  // under; only a request without one is attributed to whoever is at the till
  const staff = staffToken.get();
  if (
    staff &&
    config.url?.startsWith("/pos") &&
    !config.headers["x-staff-token"]
  ) {
    config.headers["x-staff-token"] = staff;
  }

  return config;
});

/** Raised when the API says this install is not a licensed device. */
export const isDeviceError = (error) =>
  error?.response?.status === 402 ||
  ["DEVICE_NOT_ACTIVATED", "DEVICE_REVOKED"].includes(
    error?.response?.data?.code,
  );

/** Raised when the API says this shop's account is suspended. */
export const isShopSuspendedError = (error) =>
  error?.response?.data?.code === "SHOP_SUSPENDED";

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // The till's own requests, including its PIN check: a wrong PIN answers
    // 401 too, and must not sign the dashboard out
    const isTillRequest =
      error.config?.url?.startsWith("/pos") ||
      error.config?.url === "/devices/unlock" ||
      error.config?.url === "/devices/pin" ||
      // A wrong current password answers 401 too; it is a typo, not an expiry
      error.config?.url === "/users/password";
    const onLoginPage = window.location.pathname === "/login";

    // A suspended shop is not a login problem either — every screen (the
    // register included) needs to show the operator's own message instead of
    // failing request by request.
    if (isShopSuspendedError(error)) {
      window.dispatchEvent(
        new CustomEvent("shop-suspended", {
          detail: {
            message: error.response.data.message,
            shopName: error.response.data.shop_name,
          },
        }),
      );
      return Promise.reject(error);
    }

    // A revoked or unknown device is not a login problem — the app has to show
    // the activation screen rather than bounce the user to a password form.
    if (isDeviceError(error)) {
      window.dispatchEvent(new CustomEvent("device-not-activated"));
      return Promise.reject(error);
    }

    // The register is unauthenticated, so it must never be redirected away
    // from a sale in progress by an expired admin token.
    if (error.response?.status === 401 && !isTillRequest && !onLoginPage) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// First-run wizard and the "Get started" checklist (owner only)
export const onboardingAPI = {
  get: () => api.get("/shops/current/onboarding"),
  update: (data) => api.put("/shops/current/onboarding", data),
  markStep: (key) => api.post(`/shops/current/onboarding/steps/${key}`),
};

// What the shop uses: presets and switches, within what its plan offers
export const modulesAPI = {
  get: () => api.get("/shops/current/modules"),
  update: (data) => api.put("/shops/current/modules", data),
};

// The owner's subscription: plan, next date, how to pay, payments and receipts
export const subscriptionAPI = {
  get: () => api.get("/shops/current/subscription"),
  document: (paymentId) =>
    api.get(`/shops/current/subscription/payments/${paymentId}/document`, {
      responseType: "text",
      transformResponse: (response) => response,
    }),
};

// Starter products: the operator's approved catalogue, copied into this shop
export const catalogAPI = {
  templates: () => api.get("/shops/current/catalog/templates"),
  items: (templateId, params) =>
    api.get(`/shops/current/catalog/templates/${templateId}/items`, { params }),
  copy: (data) => api.post("/shops/current/catalog/copy", data),
  requestProduct: (data) => api.post("/shops/current/catalog/requests", data),
};

// Public signup: whether it is open, and creating a shop
export const signupAPI = {
  config: () => api.get("/signup/config"),
  // Who sent an invitation link (?ref=)
  referral: (code) => api.get(`/signup/referral/${encodeURIComponent(code)}`),
  signup: (data) => api.post("/signup", data),
};

// Auth API
export const authAPI = {
  login: (identifier, password) =>
    api.post("/auth/login", { identifier, password }),
  register: (data) => api.post("/auth/register", data),
  verify: () => api.get("/auth/verify"),
  verifyEmail: (token) => api.post("/auth/verify-email", { token }),
  resendVerification: (lang) => api.post("/auth/resend-verification", { lang }),
  forgotPassword: (email, lang) =>
    api.post("/auth/forgot-password", { email, lang }),
  resetPassword: (token, new_password) =>
    api.post("/auth/reset-password", { token, new_password }),
  getProfile: () => api.get("/users/profile"),
};

// Products API
export const productsAPI = {
  getAll: (params) => api.get("/products", { params }),
  // Selling by the dose, set for every liquid in one table
  doseSetup: () => api.get("/products/dose-setup"),
  saveDoseSetup: (items) => api.put("/products/dose-setup", { items }),
  getById: (id, lang) => api.get(`/products/${id}`, { params: { lang } }),
  create: (data) => {
    const headers =
      data instanceof FormData ? { "Content-Type": "multipart/form-data" } : {};
    // ...existing code...
    console.log("=== Form Data Contents ===");
    for (let [key, value] of data.entries()) {
      // Special handling for File objects
      if (value instanceof File) {
        console.log(`${key}: [File] ${value.name} (${value.size} bytes)`);
      } else {
        console.log(`${key}:`, value);
      }
    }
    console.log("========================");
    // ...existing code...
    return api.post("/products", data, { headers });
  },
  update: (id, data) => {
    const headers =
      data instanceof FormData ? { "Content-Type": "multipart/form-data" } : {};
    return api.put(`/products/${id}`, data, { headers });
  },
  delete: (id) => api.delete(`/products/${id}`),
  // What deleting would do: sales/pre-orders/deliveries that make it archive
  // rather than erase, and any packs that stop it altogether
  getDeleteImpact: (id) => api.get(`/products/${id}/delete-impact`),
  restore: (id) => api.post(`/products/${id}/restore`),
  updateStock: (id, delta_quantity, notes) =>
    api.patch(`/products/${id}/stock`, { delta_quantity, notes }),
  getLowStock: (threshold) =>
    api.get("/products/low-stock", { params: { threshold } }),
};

// Variant groups ("ranges"): one model or brand line whose flavours or colours
// are ordinary products. Owner-only; the register gets its groups from /pos/catalog.
const multipartHeaders = (data) =>
  data instanceof FormData ? { "Content-Type": "multipart/form-data" } : {};

export const variantGroupsAPI = {
  list: () => api.get("/variant-groups"),
  get: (id) => api.get(`/variant-groups/${id}`),
  create: (data) =>
    api.post("/variant-groups", data, { headers: multipartHeaders(data) }),
  update: (id, data) =>
    api.put(`/variant-groups/${id}`, data, { headers: multipartHeaders(data) }),
  delete: (id) => api.delete(`/variant-groups/${id}`),
  addMembers: (id, labels) => api.post(`/variant-groups/${id}/members`, { labels }),
  apply: (id, fields) => api.post(`/variant-groups/${id}/apply`, { fields }),
  // A generic product becomes a range; the product is archived
  convertProduct: (productId, data) =>
    api.post(`/variant-groups/from-product/${productId}`, data),
};

// The owner's reminders: a notification at a chosen time on chosen days
export const remindersAPI = {
  list: () => api.get("/reminders"),
  create: (data) => api.post("/reminders", data),
  update: (id, data) => api.put(`/reminders/${id}`, data),
  remove: (id) => api.delete(`/reminders/${id}`),
  // Sends it now to this person's devices, to try it; the schedule is untouched
  sendNow: (id) => api.post(`/reminders/${id}/send`),
};

// Where the shelf stands against its alert levels, and what to buy
export const stockAPI = {
  overview: (params) => api.get("/stock", { params }),
  shoppingList: () => api.get("/stock/shopping-list"),
  setAlert: (productId, data) => api.put(`/stock/${productId}/alert`, data),
  // Between the shelf and the warehouse: [{ product_id, to: "warehouse" | "shop", quantity }]
  transfer: (moves) => api.post("/stock/transfers", { moves }),
  // A recount or a loss in the warehouse
  adjustWarehouse: (productId, delta, notes) =>
    api.post(`/stock/${productId}/warehouse`, { delta, notes }),
};

// Point of sale API (in-store register, no login required)
export const posAPI = {
  getCatalog: (params) => api.get("/pos/catalog", { params }),
  createSale: (data, staff) =>
    api.post(
      "/pos/sales",
      data,
      staff ? { headers: { "x-staff-token": staff } } : undefined,
    ),
  refundSale: (id, reason) => api.post(`/pos/sales/${id}/refund`, { reason }),
  pendingPickups: () => api.get("/pos/preorders"),
  // Flags an order as prepared, before the customer arrives for it
  markPreOrderReady: (id) => api.patch(`/pos/preorders/${id}/ready`),
  // The customer pays at pickup, so handing an order over records its sale
  collectPreOrder: (id, paymentMethod) =>
    api.post(`/pos/preorders/${id}/collect`, { payment_method: paymentMethod }),
  me: () => api.get("/pos/me"),
  getTodaySummary: () => api.get("/pos/summary"),
  getRecentSales: (limit) =>
    api.get("/pos/sales/recent", { params: { limit } }),
  resolveCustomer: (code) =>
    api.get(`/pos/customers/${encodeURIComponent(code)}`),
  findCustomer: (phone) => api.get("/pos/customers", { params: { phone } }),
  // An owner's PIN approving one sale refused as below cost
  approveOverride: (pin, clientSaleId) =>
    api.post("/pos/override", { pin, client_sale_id: clientSaleId }),
  // The cash drawer: blind counts, change in, cash out, closing
  drawer: () => api.get("/pos/drawer"),
  openDrawer: (counted) => api.post("/pos/drawer/open", { counted }),
  moveCash: (type, amount, note, extra = {}) =>
    api.post("/pos/drawer/movements", { type, amount, note, ...extra }),
  closeDrawer: (data) => api.post("/pos/drawer/close", data),
  // A supplier dropping stock off at the till
  suppliers: () => api.get("/pos/suppliers"),
  receiveDelivery: (data) => api.post("/pos/deliveries", data),
  payoutSupplier: (id, amount, note) =>
    api.post(`/pos/suppliers/${id}/payouts`, { amount, note }),
  // Tabs by name or phone ending, most owed first — "put it on …'s tab"
  searchTabs: (q) => api.get("/pos/credit/tabs", { params: { q } }),
  // Selling by the dose: open bottles, opening one, finishing one
  doses: () => api.get("/pos/doses"),
  openBottle: (productId) => api.post("/pos/doses/open", { product_id: productId }),
  finishBottle: (id) => api.post(`/pos/doses/${id}/finish`),
  cancelBottle: (id) => api.post(`/pos/doses/${id}/cancel`),
};

// The owner's savings: cash taken home, and what is paid out of it. Owner only.
export const savingsAPI = {
  overview: (params) => api.get("/savings", { params }),
  deposit: (data) => api.post("/savings/deposit", data),
  withdraw: (data) => api.post("/savings/withdraw", data),
};

// The cash drawer's history — for whoever may manage cash
export const cashAPI = {
  sessions: (params) => api.get("/cash/sessions", { params }),
  session: (id) => api.get(`/cash/sessions/${id}`),
};

// Suppliers — who the shop buys from, and what it owes them
export const suppliersAPI = {
  list: (params) => api.get("/suppliers", { params }),
  // Names only, for whoever records deliveries
  names: () => api.get("/suppliers/names"),
  create: (data) => api.post("/suppliers", data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  history: (id) => api.get(`/suppliers/${id}`),
  pay: (id, data) => api.post(`/suppliers/${id}/payments`, data),
};

// The shop's journal — owner-only
export const journalAPI = {
  list: (params) => api.get("/journal", { params }),
};

// Purchases API
export const purchasesAPI = {
  getAll: (params) => api.get("/purchases", { params }),
  getById: (id) => api.get(`/purchases/${id}`),
  create: (data) => api.post("/purchases", data),
  quickScan: (data) => api.post("/purchases/quick-scan", data),
  getStats: (params) => api.get("/purchases/stats", { params }),
  refund: (id, reason) => api.post(`/purchases/${id}/refund`, { reason }),
};

// Pre-orders API
export const preOrdersAPI = {
  getAll: (params) => api.get("/preorders", { params }),
  getById: (id) => api.get(`/preorders/${id}`),
  create: (data) => api.post("/preorders", data),
  // paymentMethod only matters when the status becomes 'completed'
  updateStatus: (id, status, paymentMethod) =>
    api.patch(`/preorders/${id}/status`, {
      status,
      payment_method: paymentMethod,
    }),
  complete: (id, paymentMethod) =>
    api.post(`/preorders/${id}/complete`, { payment_method: paymentMethod }),
  cancel: (id) => api.post(`/preorders/${id}/cancel`),
};

// Offers API
export const offersAPI = {
  getAll: (params) => api.get("/offers", { params }),
  getActive: (lang) => api.get("/offers/active", { params: { lang } }),
  getById: (id, lang) => api.get(`/offers/${id}`, { params: { lang } }),
  create: (data) => api.post("/offers", data),
  update: (id, data) => api.put(`/offers/${id}`, data),
  delete: (id) => api.delete(`/offers/${id}`),
  // What the offer applies to. Without this an offer is just a poster.
  setProducts: (id, product_ids, group_ids) =>
    api.put(`/offers/${id}/products`, { product_ids, group_ids }),
  toggleStatus: (id, is_active) =>
    api.patch(`/offers/${id}/status`, { is_active }),
};

// Analytics API
export const analyticsAPI = {
  getDashboard: () => api.get("/analytics/dashboard"),
  // location: "shop" | "warehouse" | "both"
  getInventoryReport: (location) =>
    api.get("/analytics/inventory", { params: location ? { location } : undefined }),
  getSalesReport: (start_date, end_date) =>
    api.get("/analytics/sales", { params: { start_date, end_date } }),
  getProductPerformance: (product_id, days) =>
    api.get(`/analytics/products/${product_id}/performance`, {
      params: { days },
    }),
  getCustomerInsights: (user_id) =>
    api.get(`/analytics/customers/${user_id}/insights`),
  getStockMovements: (params) =>
    api.get("/analytics/stock-movements", { params }),
};

// Settings API
export const settingsAPI = {
  getAll: () => api.get("/settings"),
  get: (key) => api.get(`/settings/${key}`),
  set: (key, value) => api.put(`/settings/${key}`, { value }),
  delete: (key) => api.delete(`/settings/${key}`),
};

// Users API
export const usersAPI = {
  getProfile: () => api.get("/users/profile"),
  updateProfile: (data) => api.put("/users/profile", data),
  changePassword: (current_password, new_password) =>
    api.put("/users/password", { current_password, new_password }),
  updatePreferences: (data) => api.put("/users/preferences", data),
  getFavorites: () => api.get("/users/favorites"),
  addFavorite: (product_id) => api.post(`/users/favorites/${product_id}`),
  removeFavorite: (product_id) => api.delete(`/users/favorites/${product_id}`),
  getPurchases: (params) => api.get("/users/purchases", { params }),

  // Alerts on this device when a pre-order comes in. "dashboard" keeps them
  // apart from the customer app's "your order is ready" subscriptions.
  pushStatus: () => api.get("/users/me/push", { params: { app: "dashboard" } }),
  subscribePush: (subscription) =>
    api.post("/users/me/push", { subscription, app: "dashboard" }),
  unsubscribePush: (endpoint) =>
    api.delete("/users/me/push", { data: { endpoint } }),
};

export default api;

// Devices — activation and the shop's licence
export const devicesAPI = {
  // `shopId`: activate in that shop (the user works in more than one)
  activate: (data, shopId) => api.post("/devices/activate", data, shopId ? { shopId } : undefined),
  current: () => api.get("/devices/me"),
  list: () => api.get("/devices"),
  revoke: (id, shopId) => api.delete(`/devices/${id}`, shopId ? { shopId } : undefined),
  unlock: (pin) => api.post("/devices/unlock", { pin }),
  // Sent with the staff token of whoever just unlocked the till
  changePin: (pin, staffTokenValue) =>
    api.put(
      "/devices/pin",
      { pin },
      { headers: { "x-staff-token": staffTokenValue } },
    ),
};

// Shops — profile, the shop picker, and staff
export const shopsAPI = {
  mine: () => api.get("/shops/mine"),
  current: () => api.get("/shops/current"),
  reportProblem: (data) => api.post("/shops/current/problem-reports", data),
  update: (data) => api.put("/shops/current", data),
  updateSlug: (slug) => api.put("/shops/current/slug", { slug }),
  changeMyPin: (pin) => api.put("/shops/current/my-pin", { pin }),
  staff: () => api.get("/shops/current/staff"),
  // What permissions exist, for the staff screen's editor
  permissionCatalog: () => api.get("/shops/current/permissions"),
  addStaff: (data) => api.post("/shops/current/staff", data),
  updateStaff: (id, data) => api.put(`/shops/current/staff/${id}`, data),
  removeStaff: (id) => api.delete(`/shops/current/staff/${id}`),
  resetStaffPassword: (id) =>
    api.post(`/shops/current/staff/${id}/reset-password`),
  // Copying products from another shop this owner has
  copySources: () => api.get("/shops/current/copy-sources"),
  copySourceProducts: (sourceId, params) =>
    api.get(`/shops/current/copy-sources/${sourceId}/products`, { params }),
  copyFromShop: (sourceId, data) =>
    api.post(`/shops/current/copy-sources/${sourceId}/copy`, data),
  // The owner's invitation link and the shops it brought in
  referrals: () => api.get("/shops/current/referrals"),
  // Opening another shop in the room the owner's pack plan has
  newShopRoom: () => api.get("/shops/current/new-shop"),
  openNewShop: () => api.post("/shops/current/new-shop"),
};

// Finance — the owner's private view of the business
export const financeAPI = {
  expenses: (params) => api.get("/finance/expenses", { params }),
  // Sent as multipart when a photo of the receipt is attached; axios sets the
  // boundary itself, so the content type is deliberately left alone here.
  createExpense: (data) => api.post("/finance/expenses", data),
  updateExpense: (id, data) => api.put(`/finance/expenses/${id}`, data),
  deleteExpense: (id) => api.delete(`/finance/expenses/${id}`),

  receipts: (params) => api.get("/finance/receipts", { params }),
  createReceipt: (data) => api.post("/finance/receipts", data),
  // Prices lines that arrived without one: [{ item_id, line_total }]
  priceReceipt: (id, prices) => api.put(`/finance/receipts/${id}/prices`, { prices }),

  summary: (params) => api.get("/finance/summary", { params }),
  journal: (params) => api.get("/finance/journal", { params }),
  monthly: (months) => api.get("/finance/monthly", { params: { months } }),
  margins: (params) => api.get("/finance/margins", { params }),

  credit: () => api.get("/finance/credit"),
  recordCreditPayment: (data) => api.post("/finance/credit/payments", data),
  creditBreakdown: (accountId) =>
    api.get(`/finance/credit/${accountId}/purchases`),
};

// Mixing — materials, recipes and batches (owner only)
export const mixingAPI = {
  materials: (params) => api.get("/mixing/materials", { params }),
  createMaterial: (data) => api.post("/mixing/materials", data),
  updateMaterial: (id, data) => api.put(`/mixing/materials/${id}`, data),
  deleteMaterial: (id) => api.delete(`/mixing/materials/${id}`),
  adjustMaterial: (id, delta, reason) =>
    api.patch(`/mixing/materials/${id}/stock`, { delta, reason }),

  recipes: () => api.get("/mixing/recipes"),
  recipe: (id) => api.get(`/mixing/recipes/${id}`),
  createRecipe: (data) => api.post("/mixing/recipes", data),
  updateRecipe: (id, data) => api.put(`/mixing/recipes/${id}`, data),
  deleteRecipe: (id) => api.delete(`/mixing/recipes/${id}`),

  // Costs a batch without committing it — same code path as producing one
  planBatch: (id, data) => api.post(`/mixing/recipes/${id}/plan`, data),
  produce: (id, data) => api.post(`/mixing/recipes/${id}/produce`, data),

  batches: (params) => api.get("/mixing/batches", { params }),
  readyToShelve: () => api.get("/mixing/batches/ready"),
  shelve: (id) => api.post(`/mixing/batches/${id}/shelve`),
  sellEarly: (id, data) => api.post(`/mixing/batches/${id}/sell-early`, data),
  discard: (id, reason) =>
    api.post(`/mixing/batches/${id}/discard`, { reason }),
};

// Packs — bundles sold at one price, drawn from their components' stock
export const packsAPI = {
  getAll: () => api.get("/packs"),
  create: (data) => api.post("/packs", data),
  update: (id, data) => api.put(`/packs/${id}`, data),
  remove: (id) => api.delete(`/packs/${id}`),
};

export const legalAPI = {
  operator: () => api.get("/legal/operator"),
};
